import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Palette, Upload, Printer, ArrowLeft, Loader2, Trash2, Image as ImageIcon, Brush, Droplet, Eraser, Camera, Save, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ===== helper: read uploaded file → base64 (compressed) =====
const fileToCompressedBase64 = (file, maxEdge = 1280, quality = 0.85) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', quality).split(',')[1]);
    };
    img.onerror = reject;
    img.src = reader.result;
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

// ===== flood-fill helper (4-way scanline) =====
const hexToRgba = (hex) => {
  const h = hex.replace('#', '');
  return [parseInt(h.substr(0, 2), 16), parseInt(h.substr(2, 2), 16), parseInt(h.substr(4, 2), 16), 255];
};

const floodFill = (ctx, x, y, fillRgba, tolerance = 40) => {
  const { width: w, height: h } = ctx.canvas;
  const img = ctx.getImageData(0, 0, w, h);
  const data = img.data;
  const startIdx = (y * w + x) * 4;
  const sr = data[startIdx], sg = data[startIdx + 1], sb = data[startIdx + 2];
  // Don't fill if clicked on an outline pixel (already very dark)
  if (sr + sg + sb < 90) return false;
  // Don't fill if already same color
  if (Math.abs(sr - fillRgba[0]) < 4 && Math.abs(sg - fillRgba[1]) < 4 && Math.abs(sb - fillRgba[2]) < 4) return false;

  const matches = (i) => {
    // Don't cross outline (dark pixels)
    if (data[i] + data[i + 1] + data[i + 2] < 150) return false;
    return Math.abs(data[i] - sr) <= tolerance &&
           Math.abs(data[i + 1] - sg) <= tolerance &&
           Math.abs(data[i + 2] - sb) <= tolerance;
  };
  const setPixel = (i) => {
    data[i] = fillRgba[0]; data[i + 1] = fillRgba[1]; data[i + 2] = fillRgba[2]; data[i + 3] = 255;
  };

  const stack = [[x, y]];
  let safety = 0;
  while (stack.length && safety < 4_000_000) {
    safety++;
    const [px, py] = stack.pop();
    let nx = px;
    // walk left
    while (nx >= 0 && matches((py * w + nx) * 4)) nx--;
    nx++;
    let spanUp = false, spanDown = false;
    while (nx < w && matches((py * w + nx) * 4)) {
      setPixel((py * w + nx) * 4);
      if (py > 0) {
        const m = matches(((py - 1) * w + nx) * 4);
        if (!spanUp && m) { stack.push([nx, py - 1]); spanUp = true; }
        else if (spanUp && !m) spanUp = false;
      }
      if (py < h - 1) {
        const m = matches(((py + 1) * w + nx) * 4);
        if (!spanDown && m) { stack.push([nx, py + 1]); spanDown = true; }
        else if (spanDown && !m) spanDown = false;
      }
      nx++;
    }
  }
  ctx.putImageData(img, 0, 0);
  return true;
};

// ===== Paint canvas component =====
function PaintCanvas({ project, onSave, onPrint, onBack }) {
  const canvasRef = useRef(null);
  const outlineImgRef = useRef(null);
  const [selectedColor, setSelectedColor] = useState(project.palette?.[0]?.hex || '#FFC8DD');
  const [selectedNumber, setSelectedNumber] = useState(project.palette?.[0]?.number || 1);
  const [tool, setTool] = useState('fill'); // 'fill' | 'brush' | 'eraser'
  const [brushSize, setBrushSize] = useState(12);
  const [saving, setSaving] = useState(false);
  const [filled, setFilled] = useState(project.filled_regions || {});
  const isDrawingRef = useRef(false);
  const lastPosRef = useRef(null);
  const saveTimerRef = useRef(null);

  // Draw outline (and saved snapshot if exists) onto canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      // Cap canvas at 900px to keep flood-fill fast
      const maxEdge = 900;
      const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      // Fill white background first (so flood fill has a clean start)
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      outlineImgRef.current = img;

      // Overlay saved snapshot if any
      if (project.snapshot_base64) {
        const snap = new Image();
        snap.onload = () => {
          ctx.drawImage(snap, 0, 0, canvas.width, canvas.height);
        };
        snap.src = `data:${project.snapshot_mime || 'image/png'};base64,${project.snapshot_base64}`;
      }
    };
    img.src = `data:${project.outline_mime || 'image/png'};base64,${project.outline_image_base64}`;
  }, [project]);

  const scheduleSave = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      setSaving(true);
      try {
        // Downscale snapshot to ~600px for storage
        const snap = document.createElement('canvas');
        const scale = Math.min(1, 600 / Math.max(canvas.width, canvas.height));
        snap.width = Math.round(canvas.width * scale);
        snap.height = Math.round(canvas.height * scale);
        snap.getContext('2d').drawImage(canvas, 0, 0, snap.width, snap.height);
        const b64 = snap.toDataURL('image/png').split(',')[1];
        await axios.post(`${API}/paint/projects/${project.id}/progress`, {
          filled_regions: filled,
          snapshot_base64: b64,
        }, { withCredentials: true });
      } catch (e) {
        // silent
      } finally {
        setSaving(false);
      }
    }, 1100);
  }, [filled, project.id]);

  const getCanvasPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const x = Math.round((clientX - rect.left) * canvas.width / rect.width);
    const y = Math.round((clientY - rect.top) * canvas.height / rect.height);
    return { x: Math.max(0, Math.min(canvas.width - 1, x)), y: Math.max(0, Math.min(canvas.height - 1, y)) };
  };

  const handleCanvasClick = (e) => {
    if (tool !== 'fill') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const { x, y } = getCanvasPos(e);
    const ok = floodFill(ctx, x, y, hexToRgba(selectedColor), 50);
    if (ok) {
      setFilled((f) => ({ ...f, [String(selectedNumber)]: selectedColor }));
      scheduleSave();
    }
  };

  const startDraw = (e) => {
    if (tool === 'fill') return;
    e.preventDefault();
    isDrawingRef.current = true;
    lastPosRef.current = getCanvasPos(e);
  };

  const moveDraw = (e) => {
    if (!isDrawingRef.current || tool === 'fill') return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasPos(e);
    const last = lastPosRef.current || { x, y };
    ctx.strokeStyle = tool === 'eraser' ? '#ffffff' : selectedColor;
    ctx.lineWidth = brushSize;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.globalAlpha = tool === 'eraser' ? 1 : 0.85;
    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(x, y);
    ctx.stroke();
    ctx.globalAlpha = 1;
    lastPosRef.current = { x, y };
  };

  const endDraw = () => {
    if (!isDrawingRef.current) return;
    isDrawingRef.current = false;
    lastPosRef.current = null;
    scheduleSave();
  };

  const resetCanvas = () => {
    if (!window.confirm('Börja om från början? All målning försvinner 💛')) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    if (outlineImgRef.current) {
      ctx.drawImage(outlineImgRef.current, 0, 0, canvas.width, canvas.height);
    }
    setFilled({});
    scheduleSave();
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 p-3 bg-white/70 backdrop-blur rounded-2xl shadow-sm border border-pink-100">
        <button
          onClick={onBack}
          data-testid="paint-back-btn"
          className="px-3 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-sm font-medium flex items-center gap-1"
        >
          <ArrowLeft className="w-4 h-4" /> Tillbaka
        </button>
        <div className="h-6 w-px bg-gray-200" />
        <button
          onClick={() => setTool('fill')}
          data-testid="paint-tool-fill"
          className={`px-3 py-2 rounded-xl text-sm font-medium flex items-center gap-1 transition ${tool === 'fill' ? 'bg-pink-500 text-white shadow' : 'bg-pink-50 text-pink-700 hover:bg-pink-100'}`}
        >
          <Droplet className="w-4 h-4" /> Hink
        </button>
        <button
          onClick={() => setTool('brush')}
          data-testid="paint-tool-brush"
          className={`px-3 py-2 rounded-xl text-sm font-medium flex items-center gap-1 transition ${tool === 'brush' ? 'bg-purple-500 text-white shadow' : 'bg-purple-50 text-purple-700 hover:bg-purple-100'}`}
        >
          <Brush className="w-4 h-4" /> Pensel
        </button>
        <button
          onClick={() => setTool('eraser')}
          data-testid="paint-tool-eraser"
          className={`px-3 py-2 rounded-xl text-sm font-medium flex items-center gap-1 transition ${tool === 'eraser' ? 'bg-gray-500 text-white shadow' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}
        >
          <Eraser className="w-4 h-4" /> Sudd
        </button>
        {(tool === 'brush' || tool === 'eraser') && (
          <div className="flex items-center gap-2 px-2">
            <span className="text-xs text-gray-500">Storlek</span>
            <input
              type="range" min="3" max="40" value={brushSize}
              onChange={(e) => setBrushSize(parseInt(e.target.value))}
              className="w-24"
              data-testid="paint-brush-size"
            />
            <span className="text-xs w-6 text-gray-700">{brushSize}</span>
          </div>
        )}
        <div className="h-6 w-px bg-gray-200" />
        <button
          onClick={resetCanvas}
          data-testid="paint-reset-btn"
          className="px-3 py-2 rounded-xl bg-rose-50 text-rose-700 hover:bg-rose-100 text-sm font-medium flex items-center gap-1"
        >
          <RotateCcw className="w-4 h-4" /> Börja om
        </button>
        <button
          onClick={onPrint}
          data-testid="paint-print-btn"
          className="px-3 py-2 rounded-xl bg-amber-100 text-amber-800 hover:bg-amber-200 text-sm font-medium flex items-center gap-1"
        >
          <Printer className="w-4 h-4" /> Skriv ut
        </button>
        <div className="ml-auto flex items-center gap-2 text-xs text-gray-500">
          {saving ? (
            <><Loader2 className="w-3 h-3 animate-spin" /> Sparar…</>
          ) : (
            <><Save className="w-3 h-3 text-green-500" /> Sparat</>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_220px] gap-4">
        {/* Canvas */}
        <div className="bg-white rounded-2xl shadow-sm border border-pink-100 p-3 flex items-center justify-center overflow-hidden">
          <canvas
            ref={canvasRef}
            data-testid="paint-canvas"
            onClick={handleCanvasClick}
            onMouseDown={startDraw}
            onMouseMove={moveDraw}
            onMouseUp={endDraw}
            onMouseLeave={endDraw}
            onTouchStart={startDraw}
            onTouchMove={moveDraw}
            onTouchEnd={endDraw}
            className="max-w-full h-auto rounded-xl"
            style={{ cursor: tool === 'fill' ? 'crosshair' : 'cell', touchAction: 'none' }}
          />
        </div>

        {/* Palette */}
        <div className="bg-white rounded-2xl shadow-sm border border-pink-100 p-4 space-y-2">
          <h3 className="font-bold text-pink-700 flex items-center gap-1.5 text-sm">
            <Palette className="w-4 h-4" /> Färgpalett
          </h3>
          <div className="space-y-1.5">
            {(project.palette || []).map((p) => {
              const isActive = p.number === selectedNumber;
              const isDone = filled[String(p.number)];
              return (
                <button
                  key={p.number}
                  onClick={() => { setSelectedColor(p.hex); setSelectedNumber(p.number); }}
                  data-testid={`paint-color-${p.number}`}
                  className={`w-full flex items-center gap-2 p-2 rounded-xl transition border-2 ${isActive ? 'border-pink-500 bg-pink-50 shadow-sm' : 'border-transparent hover:border-pink-200 bg-gray-50'}`}
                >
                  <span
                    className="w-9 h-9 rounded-lg shadow-inner flex items-center justify-center text-xs font-bold border border-black/10"
                    style={{ background: p.hex, color: '#222' }}
                  >
                    {p.number}
                  </span>
                  <div className="flex-1 text-left">
                    <div className="text-xs font-semibold text-gray-700 truncate">{p.name}</div>
                    <div className="text-[10px] text-gray-400 font-mono">{p.hex}</div>
                  </div>
                  {isDone && <span className="text-green-500 text-sm">✓</span>}
                </button>
              );
            })}
          </div>
          <div className="pt-2 mt-2 border-t border-gray-100 text-[11px] text-gray-500 leading-tight">
            💡 <strong>Hink</strong>: klicka i en region. <strong>Pensel</strong>: dra för att måla fritt.
          </div>
        </div>
      </div>
    </div>
  );
}

// ===== Print view =====
function PrintView({ project, onBack }) {
  const printRef = useRef(null);
  const handlePrint = () => {
    window.print();
  };
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 print:hidden">
        <button
          onClick={onBack}
          data-testid="paint-print-back"
          className="px-3 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-sm font-medium flex items-center gap-1"
        >
          <ArrowLeft className="w-4 h-4" /> Tillbaka
        </button>
        <button
          onClick={handlePrint}
          data-testid="paint-print-go"
          className="px-4 py-2 rounded-xl bg-amber-400 hover:bg-amber-500 text-white text-sm font-bold flex items-center gap-1 shadow"
        >
          <Printer className="w-4 h-4" /> Skriv ut nu
        </button>
        <p className="text-xs text-gray-500 ml-2">Färdig bild + outline + färglegend — perfekt för pennor och kritor 🖍️</p>
      </div>
      <div ref={printRef} className="bg-white p-6 rounded-2xl shadow print-page">
        <div className="text-center mb-4">
          <h2 className="text-2xl font-bold text-pink-700">{project.title}</h2>
          <p className="text-xs text-gray-500 italic">Måla efter nummer · Focus Critter 🎨</p>
        </div>
        <div className="grid grid-cols-2 gap-4 mb-4">
          {project.reference_image_base64 && (
            <div className="text-center">
              <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-1">Så ser den ut färdig</p>
              <img
                src={`data:${project.reference_mime || 'image/png'};base64,${project.reference_image_base64}`}
                alt="Färdig bild"
                className="rounded-lg shadow-sm w-full"
              />
            </div>
          )}
          <div className="text-center">
            <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-1">Måla denna</p>
            <img
              src={`data:${project.outline_mime || 'image/png'};base64,${project.outline_image_base64}`}
              alt="Outline"
              className="rounded-lg shadow-sm w-full bg-white"
            />
          </div>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">Färglegend</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {(project.palette || []).map((p) => (
              <div key={p.number} className="flex items-center gap-2 p-2 rounded-lg border border-gray-200">
                <span
                  className="w-9 h-9 rounded-md flex items-center justify-center text-xs font-bold border border-black/20"
                  style={{ background: p.hex, color: '#222' }}
                >
                  {p.number}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-gray-700 truncate">{p.name}</div>
                  <div className="text-[10px] text-gray-400 font-mono">{p.hex}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <style>{`
        @media print {
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
          .print-page { box-shadow: none !important; page-break-inside: avoid; }
          header, nav, aside, footer { display: none !important; }
        }
      `}</style>
    </div>
  );
}

// ===== Main page =====
export default function PaintByNumbersPage() {
  const [view, setView] = useState('gallery'); // gallery | upload | paint | print
  const [presets, setPresets] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loadingPresetKey, setLoadingPresetKey] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const loadAll = useCallback(async () => {
    try {
      const [p, pr] = await Promise.all([
        axios.get(`${API}/paint/presets`, { withCredentials: true }),
        axios.get(`${API}/paint/projects`, { withCredentials: true }),
      ]);
      setPresets(p.data?.presets || []);
      setProjects(pr.data?.items || []);
    } catch (e) {
      toast.error('Kunde inte hämta målningar 💔');
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openPreset = async (key) => {
    setLoadingPresetKey(key);
    try {
      // First fetch (generates + caches in DB if first time — takes ~20-30s)
      const r = await axios.get(`${API}/paint/presets/${key}`, { withCredentials: true, timeout: 120000 });
      const preset = r.data?.preset;
      if (!preset) throw new Error('Tomt svar');
      // Create user project linked to this preset
      const proj = await axios.post(`${API}/paint/projects/from-preset/${key}`, {}, { withCredentials: true });
      setCurrentProject(proj.data?.project);
      setView('paint');
      loadAll();
    } catch (e) {
      toast.error('Kunde inte skapa målarbilden 💔 ' + (e?.response?.data?.detail || ''));
    } finally {
      setLoadingPresetKey(null);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      toast.info('Bearbetar fotot… 🎨 (~30 sek)', { duration: 30000 });
      const b64 = await fileToCompressedBase64(file, 1280, 0.85);
      const r = await axios.post(`${API}/paint/from-photo`, {
        photo_base64: b64,
        title: file.name?.replace(/\.[^.]+$/, '') || 'Mitt foto',
      }, { withCredentials: true, timeout: 120000 });
      const proj = r.data?.project;
      if (!proj) throw new Error('Tomt svar');
      setCurrentProject(proj);
      setView('paint');
      toast.dismiss();
      toast.success('Klart! Börja måla 💖');
      loadAll();
    } catch (e) {
      toast.dismiss();
      toast.error('Kunde inte bearbeta fotot 💔 ' + (e?.response?.data?.detail || ''));
    } finally {
      setUploading(false);
    }
  };

  const openProject = async (pid) => {
    try {
      const r = await axios.get(`${API}/paint/projects/${pid}`, { withCredentials: true });
      const proj = r.data?.project;
      if (!proj) throw new Error('Tomt svar');
      setCurrentProject(proj);
      setView('paint');
    } catch (e) {
      toast.error('Kunde inte ladda projektet 💔');
    }
  };

  const deleteProject = async (pid, e) => {
    e?.stopPropagation();
    if (!window.confirm('Ta bort denna målning?')) return;
    try {
      await axios.delete(`${API}/paint/projects/${pid}`, { withCredentials: true });
      toast.success('Borttagen 💛');
      loadAll();
    } catch (e) {
      toast.error('Kunde inte ta bort');
    }
  };

  // ===== Paint view =====
  if (view === 'paint' && currentProject) {
    return (
      <PaintCanvas
        project={currentProject}
        onBack={() => { setCurrentProject(null); setView('gallery'); loadAll(); }}
        onPrint={() => setView('print')}
        onSave={loadAll}
      />
    );
  }

  if (view === 'print' && currentProject) {
    return <PrintView project={currentProject} onBack={() => setView('paint')} />;
  }

  // ===== Gallery / Upload view =====
  return (
    <div className="space-y-6" data-testid="paint-by-numbers-page">
      <div className="bg-gradient-to-br from-pink-100 via-purple-50 to-amber-50 rounded-3xl p-6 shadow-sm border border-pink-100">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="text-5xl">🎨</div>
          <div className="flex-1 min-w-[200px]">
            <h1 className="text-2xl font-bold text-pink-700 flex items-center gap-2">
              Måla efter nummer
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Lugnande pyssel för hjärnan 💛 — välj ett kawaii-djur, eller ladda upp ett eget foto.
              Du kan måla digitalt eller skriva ut och måla med riktiga pennor.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="paint-upload-btn"
              className="px-4 py-2.5 rounded-xl bg-white shadow hover:shadow-md border-2 border-pink-200 hover:border-pink-400 text-pink-700 font-bold text-sm flex items-center gap-2 disabled:opacity-50"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Ladda upp foto
            </button>
            <button
              onClick={() => cameraInputRef.current?.click()}
              disabled={uploading}
              data-testid="paint-camera-btn"
              className="px-4 py-2.5 rounded-xl bg-white shadow hover:shadow-md border-2 border-purple-200 hover:border-purple-400 text-purple-700 font-bold text-sm flex items-center gap-2 disabled:opacity-50"
            >
              <Camera className="w-4 h-4" /> Ta foto
            </button>
            <input
              ref={fileInputRef} type="file" accept="image/*" className="hidden"
              onChange={(e) => handleUpload(e.target.files?.[0])}
              data-testid="paint-file-input"
            />
            <input
              ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden"
              onChange={(e) => handleUpload(e.target.files?.[0])}
              data-testid="paint-camera-input"
            />
          </div>
        </div>
      </div>

      {/* My ongoing projects */}
      {projects.length > 0 && (
        <div>
          <h2 className="text-lg font-bold text-purple-700 mb-3 flex items-center gap-2">
            <ImageIcon className="w-5 h-5" /> Mina målningar
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="paint-projects-grid">
            {projects.map((p) => (
              <div
                key={p.id}
                onClick={() => openProject(p.id)}
                data-testid={`paint-project-${p.id}`}
                className="group cursor-pointer bg-white rounded-2xl shadow hover:shadow-lg transition border border-pink-100 hover:border-pink-300 overflow-hidden relative"
              >
                <div className="aspect-square bg-gradient-to-br from-pink-50 to-purple-50 flex items-center justify-center text-3xl">
                  {p.kind === 'from_photo' ? '📸' : '🐾'}
                </div>
                <div className="p-2">
                  <div className="text-xs font-bold text-gray-700 truncate">{p.title}</div>
                  <div className="text-[10px] text-gray-400">
                    {p.kind === 'from_photo' ? 'Eget foto' : 'Kawaii preset'}
                  </div>
                </div>
                <button
                  onClick={(e) => deleteProject(p.id, e)}
                  data-testid={`paint-project-delete-${p.id}`}
                  className="absolute top-1.5 right-1.5 p-1.5 rounded-full bg-white/90 opacity-0 group-hover:opacity-100 transition shadow hover:bg-rose-50 text-rose-500"
                  title="Ta bort"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Kawaii preset gallery */}
      <div>
        <h2 className="text-lg font-bold text-pink-700 mb-3 flex items-center gap-2">
          🐾 Kawaii-galleri
          <span className="text-xs font-normal text-gray-500">— välj ett djur att måla</span>
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="paint-presets-grid">
          {presets.map((p) => {
            const isLoading = loadingPresetKey === p.key;
            return (
              <button
                key={p.key}
                onClick={() => openPreset(p.key)}
                disabled={!!loadingPresetKey}
                data-testid={`paint-preset-${p.key}`}
                className="group bg-white rounded-2xl shadow hover:shadow-lg transition border-2 border-transparent hover:border-pink-300 p-4 text-center disabled:opacity-50 disabled:cursor-wait"
              >
                <div className="text-5xl mb-2 group-hover:scale-110 transition-transform inline-block">
                  {isLoading ? '🎨' : p.emoji}
                </div>
                <div className="text-sm font-bold text-gray-700">{p.name}</div>
                {isLoading && (
                  <div className="text-[10px] text-pink-500 mt-1 flex items-center justify-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" /> Förbereder…
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-gray-400 mt-3 italic">
          ✨ Första gången du väljer ett djur tar AI:n ~20-30 sekunder på sig att rita upp den.
          Efter det är den sparad och öppnas direkt 💖
        </p>
      </div>
    </div>
  );
}
